import { useState, useEffect, useCallback } from 'react';
import { useNotifications } from './useNotifications';

const NOTIFICATION_PROMPT_DISMISSED_KEY = 'shipmentCocaptain_notificationPromptDismissed';
const NOTIFICATION_PROMPT_SHOWN_KEY = 'shipmentCocaptain_notificationPromptShown';

export interface UseNotificationPromptReturn {
  shouldShowPrompt: boolean;
  showPrompt: () => void;
  dismissPrompt: () => void;
  enableNotifications: () => void;
  hasBeenDismissed: boolean;
  resetPromptState: () => void;
}

/**
 * Hook to manage when to show the notification permission prompt
 */
export function useNotificationPrompt(): UseNotificationPromptReturn {
  const { notificationState } = useNotifications();
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);

  // Check if prompt has been dismissed
  const hasBeenDismissed = localStorage.getItem(NOTIFICATION_PROMPT_DISMISSED_KEY) === 'true';

  // Show prompt when appropriate conditions are met
  const showPrompt = useCallback(() => {
    // Don't show if already dismissed, not supported, or already granted
    if (hasBeenDismissed || 
        !notificationState.supported || 
        notificationState.permission === 'granted' ||
        notificationState.permission === 'denied') {
      return;
    }

    setShouldShowPrompt(true);
    localStorage.setItem(NOTIFICATION_PROMPT_SHOWN_KEY, 'true');
  }, [hasBeenDismissed, notificationState.supported, notificationState.permission]);

  // Dismiss prompt
  const dismissPrompt = useCallback(() => {
    setShouldShowPrompt(false);
    localStorage.setItem(NOTIFICATION_PROMPT_DISMISSED_KEY, 'true');
  }, []);

  // Enable notifications (user clicked "Enable")
  const enableNotifications = useCallback(() => {
    setShouldShowPrompt(false);
    // Don't mark as dismissed since they enabled it
  }, []);

  // Reset prompt state (for testing or settings reset)
  const resetPromptState = useCallback(() => {
    localStorage.removeItem(NOTIFICATION_PROMPT_DISMISSED_KEY);
    localStorage.removeItem(NOTIFICATION_PROMPT_SHOWN_KEY);
    setShouldShowPrompt(false);
  }, []);

  // Auto-hide prompt if notifications become granted or denied
  useEffect(() => {
    if (notificationState.permission === 'granted' || notificationState.permission === 'denied') {
      setShouldShowPrompt(false);
    }
  }, [notificationState.permission]);

  return {
    shouldShowPrompt,
    showPrompt,
    dismissPrompt,
    enableNotifications,
    hasBeenDismissed,
    resetPromptState,
  };
}
